// apps/prompts/src/hooks/useFeedback.ts
import type { FeedbackStatus, FeedbackType, IFeedback } from '@neuroforge/shared'
import { api, unwrapResponse as unwrap } from '@neuroforge/shared/client'
import { toast } from '@neuroforge/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

interface FeedbackListResponse {
  data: IFeedback[]
  meta: { total: number; newCount: number }
}

const feedbackKeys = {
  all: ['admin', 'feedback'] as const,
  list: (filters: { type?: FeedbackType; status?: FeedbackStatus }) =>
    [...feedbackKeys.all, 'list', filters] as const,
  newCount: () => [...feedbackKeys.all, 'newCount'] as const,
}

export function useAdminFeedbacks(type?: FeedbackType, status?: FeedbackStatus) {
  return useQuery({
    queryKey: feedbackKeys.list({ type, status }),
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (type) params.type = type
      if (status) params.status = status
      const res = await api.get('/feedback', { params })
      return unwrap<FeedbackListResponse>(res)
    },
    staleTime: 30_000,
  })
}

export function useAdminFeedbackNewCount() {
  // Réutilise le cache de la liste complète si elle est déjà chargée.
  return useQuery({
    queryKey: feedbackKeys.newCount(),
    queryFn: async () => {
      const res = await api.get('/feedback')
      const data = unwrap<FeedbackListResponse>(res)
      return data?.meta?.newCount ?? 0
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    // Si la liste complète est dans le cache, on évite un appel réseau redondant.
    placeholderData: (prev) => prev,
  })
}

export function useUpdateFeedbackStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: FeedbackStatus }) => {
      const res = await api.patch(`/feedback/${id}/status`, { status })
      return unwrap<IFeedback>(res)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: feedbackKeys.all })
      toast({ type: 'success', message: 'Statut mis à jour.' })
    },
    onError: () => {
      toast({ type: 'error', message: 'Impossible de mettre à jour le statut.' })
    },
  })
}
